import type { TreeNode } from "@atlas-int/ui";

/**
 * Product hierarchy level constants.
 * Values match the canonical ProductLevel enum from @atlas-int/data so that
 * callers can pass either these constants or the enum — they are interchangeable.
 */
export const ProductItemLevel = {
  ProductSection: 1,
  Product2digit: 2,
  Product4digit: 4,
  Product6digit: 6,
  AllProducts: 9,
} as const;
export type ProductItemLevel = (typeof ProductItemLevel)[keyof typeof ProductItemLevel];

/** Minimal product parent reference (replaces ProductMetadatum dependency). */
export interface ProductParentRef {
  productId: string;
  [key: string]: unknown;
}

export interface ProductItem {
  id: string;
  name_short_en: string;
  code?: string;
  level: number | string;
  product_section?: ProductParentRef;
  product_twoDigit?: ProductParentRef;
  product_fourDigit?: ProductParentRef;
  product_sixDigit?: ProductParentRef;
  isSticky?: boolean;
  [key: string]: unknown;
}

export const buildProductTree = (flatProducts: ProductItem[]): TreeNode[] => {
  const tree: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  // First pass: Create all nodes
  flatProducts.forEach((product) => {
    const node: TreeNode = {
      id: product.id,
      name: product.name_short_en,
      children: [],
      data: product,
      ...product,
    };
    nodeMap.set(product.id, node);
  });

  // Second pass: Build tree hierarchy
  flatProducts.forEach((product) => {
    const node = nodeMap.get(product.id);
    if (!node) return;

    if (product.level === ProductItemLevel.AllProducts) {
      // All Products is root level
      tree.push(node);
    } else if (product.level === ProductItemLevel.ProductSection) {
      // Sections are root level
      tree.push(node);
    } else if (product.level === ProductItemLevel.Product2digit) {
      // 2-digit are children of sections
      const parentId = product.product_section?.productId;
      const parent = nodeMap.get(parentId!);
      if (parent) {
        parent.children!.push(node);
      }
    } else if (product.level === ProductItemLevel.Product4digit) {
      // 4-digit are children of 2-digit
      const parentId = product.product_twoDigit?.productId;
      const parent = nodeMap.get(parentId!);
      if (parent) {
        parent.children!.push(node);
      }
    } else if (product.level === ProductItemLevel.Product6digit) {
      // 6-digit are children of 4-digit
      const parentId = product.product_fourDigit?.productId;
      const parent = nodeMap.get(parentId!);
      if (parent) {
        parent.children!.push(node);
      }
    }
  });

  return tree;
};

export const getInitialExpandedProductIds = (
  _flatProducts: ProductItem[],
  selectedProduct?: ProductItem,
  allProductsId?: string
): Set<string> => {
  const expandedIds = new Set<string>();

  if (!selectedProduct || (allProductsId && selectedProduct.id === allProductsId)) {
    return expandedIds;
  }

  // Expand the full path to the selected product
  if (selectedProduct.product_section) {
    expandedIds.add(selectedProduct.product_section.productId);
  }
  if (selectedProduct.product_twoDigit) {
    expandedIds.add(selectedProduct.product_twoDigit.productId);
  }
  if (selectedProduct.product_fourDigit) {
    expandedIds.add(selectedProduct.product_fourDigit.productId);
  }

  return expandedIds;
};

export const productSearchFunction = (
  item: ProductItem,
  searchTerm: string
): boolean => {
  const searchString = `${item.name_short_en} ${item.code || ""}`.toLowerCase();
  return searchString.includes(searchTerm);
};

export const getLabelForProduct = (
  item: ProductItem,
  productClassLabel: string,
  allProductsId?: string
): string => {
  if (allProductsId && item.id === allProductsId) {
    return `${item.name_short_en} (${productClassLabel})`;
  }
  return `${item.name_short_en} (${item.code} ${productClassLabel})`;
};

export const getProductClassShortLabel = (productClass: string): string => {
  return productClass;
};
